// Wheel (pie chart) readings built from the CareDaily `trends` time series.
//
// trends_recently is generated once a day and covers the last 7 days excluding
// today, so a newly logged activity can't show on the wheel until tomorrow.
// CareDaily (Destry, 2026-10-23) asked us to build the wheel from the `trends`
// time series instead: the last 30 days including today, averaging each day's
// `avg`. This follows the method in their "Moorings Park GoIcon Wheel
// Calculation" notebook exactly, including counting the 30 days back from the
// newest trends state rather than from the clock.
//
// The output has the same shape as trends_recently ({ avg, n } per metric key),
// so computeDimensionScore reads it unchanged.

export const WHEEL_WINDOW_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

export interface WheelReading {
  avg: number;
  n: number;
}

interface TimeSeriesState {
  name?: string;
  stateDateMs?: number;
  value?: Record<string, any>;
}

export function buildWheelTrends(
  states: TimeSeriesState[] | null | undefined,
  windowDays: number = WHEEL_WINDOW_DAYS
): Record<string, WheelReading> {
  const trends = (states ?? []).filter(
    s => s?.name === 'trends' && typeof s.stateDateMs === 'number' && s.value
  );
  if (trends.length === 0) return {};

  const newestMs = Math.max(...trends.map(s => s.stateDateMs as number));
  const cutoffMs = newestMs - windowDays * DAY_MS;

  const sums: Record<string, { total: number; n: number }> = {};
  for (const state of trends) {
    if ((state.stateDateMs as number) < cutoffMs) continue;
    for (const [key, reading] of Object.entries(state.value as Record<string, any>)) {
      const avg = reading?.avg;
      if (typeof avg !== 'number' || !isFinite(avg)) continue;
      const entry = (sums[key] ??= { total: 0, n: 0 });
      entry.total += avg;
      entry.n += 1;
    }
  }

  const result: Record<string, WheelReading> = {};
  for (const [key, { total, n }] of Object.entries(sums)) {
    result[key] = { avg: total / n, n };
  }
  return result;
}
